import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { QrAPI } from "../services/api";
import { isQrActive } from "../utils/qrStatus";

// stop scanner ปลอดภัย: เช็ค state ก่อน, swallow error
async function safeStop(scanner) {
  if (!scanner) return;
  try {
    const st = scanner.getState?.();
    if (
      st === Html5QrcodeScannerState.SCANNING ||
      st === Html5QrcodeScannerState.PAUSED
    ) {
      await scanner.stop();
    }
  } catch {
    /* ignore */
  }
  try {
    scanner.clear();
  } catch {
    /* ignore */
  }
}

/**
 * Modal สแกน QR แบบ in-app — ใช้กล้องของอุปกรณ์ผ่าน html5-qrcode
 *
 * Props:
 *   onClose()          — ปิด modal
 *   onResolved(ctx)    — เรียกเมื่อสแกนสำเร็จ + resolve กับ backend
 *                        ctx = { qr, floor, building }
 *
 * รองรับ payload 2 รูปแบบ:
 *   1) URL  เช่น  https://<host>/scan/<code>
 *   2) raw code  เช่น  QR-001
 */
export default function QrScannerModal({ onClose, onResolved }) {
  const elRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | scanning | resolving | error
  const [errMsg, setErrMsg] = useState("");
  const [manualCode, setManualCode] = useState("");

  // start camera (StrictMode-safe)
  const startPromiseRef = useRef(null);
  const handledRef = useRef(false);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      if (event.reason?.name === "AbortError") {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode("qr-reader-region");
    scannerRef.current = scanner;

    const startP = scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          if (cancelled || handledRef.current) return;
          handledRef.current = true;
          handleDecoded(decoded);
        },
        () => {} // ignore per-frame errors
      )
      .then(() => {
        if (cancelled) {
          // ถูก unmount ก่อนเปิดกล้องเสร็จ → หยุดเลย
          return safeStop(scanner);
        }
        setStatus("scanning");
      })
      .catch((e) => {
        if (cancelled) return;
        setErrMsg(e?.message || "ไม่สามารถเปิดกล้องได้");
        setStatus("error");
      });
    startPromiseRef.current = startP;

    return () => {
      cancelled = true;
      // รอ start() เสร็จก่อนค่อย stop เพื่อกัน "Cannot stop, scanner is not running"
      startP.finally(() => safeStop(scanner));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractCode = (raw) => {
    if (!raw) return null;
    const text = String(raw).trim();
    // ถ้าเป็น URL → ดึง segment สุดท้ายหลัง /scan/
    const m = text.match(/\/scan\/([^/?#]+)/i);
    if (m) return decodeURIComponent(m[1]);
    // ถ้าเป็น URL ทั่วไป → ลอง path สุดท้าย
    try {
      const u = new URL(text);
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length) return decodeURIComponent(parts[parts.length - 1]);
    } catch {
      /* ignore */
    }
    return text; // ถือว่าเป็น code ดิบ
  };

  const handleDecoded = async (raw) => {
    // หยุดกล้องทันทีกัน trigger ซ้ำ
    await safeStop(scannerRef.current);
    setStatus("resolving");

    try {
      const code = extractCode(raw);
      if (!code) throw new Error("อ่าน QR ไม่สำเร็จ");

      const res = await QrAPI.getByCode(code);
      const qr = res?.qr || res;
      const floor = res?.floor;
      const building = res?.building;
      if (!qr?.id) throw new Error("ไม่พบ QR Code นี้ในระบบ");
      if (!isQrActive(qr)) throw new Error("QR Code นี้ถูกปิดใช้งานแล้ว");

      // log scan (fire-and-forget)
      const sessionId = getSessionId();
      QrAPI.scan(qr.id, sessionId, navigator.userAgent).catch(() => {});

      onResolved?.({ qr, floor, building });
    } catch (e) {
      setErrMsg(e?.message || "อ่าน QR ไม่สำเร็จ");
      setStatus("error");
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    handleDecoded(manualCode.trim());
  };

  return (
    <div className="fixed inset-0 z-[2000] bg-black/85 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between text-white mb-3">
          <div className="font-bold">สแกน QR เพื่ออัปเดตตำแหน่ง</div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* viewfinder */}
        <div className="relative w-full aspect-square bg-black rounded-2xl overflow-hidden">
          <div id="qr-reader-region" ref={elRef} className="w-full h-full" />
          {/* overlay frame */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-3/5 h-3/5 border-2 border-white/80 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>

          {status === "starting" && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              กำลังเปิดกล้อง...
            </div>
          )}
          {status === "resolving" && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40">
              กำลังอ่านข้อมูล...
            </div>
          )}
        </div>

        {status === "error" && (
          <div className="mt-3 bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 text-sm">
            {errMsg}
          </div>
        )}

        {/* manual fallback */}
        <form
          onSubmit={submitManual}
          className="mt-3 bg-white/95 rounded-xl p-3 flex gap-2"
        >
          <input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="หรือพิมพ์รหัส QR (เช่น QR-001)"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm"
          />
          <button
            type="submit"
            className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold"
          >
            ตกลง
          </button>
        </form>
      </div>
    </div>
  );
}

function getSessionId() {
  const KEY = "indoor_nav_session_id";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, sid);
  }
  return sid;
}
